// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Download, Printer } from "lucide-react";
import EmailGate from "@/components/live-sheets/EmailGate";
import { parseLocalDate } from '@/utils/formatters';
import { formatCalendarDate } from "@/lib/dates";

const CYAN = "#06B6D4";

const fmtMoney = (n: any) =>
  n == null || n === "" || isNaN(Number(n))
    ? "$0"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n));

const fmtRange = (a: any, b: any) => {
  if (!a) return "—";
  const f = (d: any) => formatCalendarDate(d, "MMM d");
  return b && b !== a ? `${f(a)}–${f(b)}` : f(a);
};

const COLS = [
  { key: "name", label: "Event", width: "w-64", type: "text" },
  { key: "dates", label: "Dates", width: "w-32", type: "dates" },
  { key: "location", label: "Location", width: "w-40", type: "location" },
  { key: "teams", label: "Teams", width: "w-20", type: "number" },
  { key: "paid", label: "Paid", width: "w-20", type: "number" },
  { key: "pending", label: "Pending", width: "w-20", type: "number" },
  { key: "collected", label: "Collected $", width: "w-28", type: "money" },
  { key: "owed", label: "Owed $", width: "w-28", type: "money" },
];

export default function EventSummarySheetPage({ tokenOverride, embedded = false }: { tokenOverride?: string; embedded?: boolean } = {}) {
  const params = useParams<{ token: string }>();
  const token = tokenOverride ?? params.token;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gatePassed, setGatePassed] = useState(false);

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<string>("start_date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const fetchData = useCallback(async () => {
    if (!token) return;
    const { data: result, error: rpcErr } = await supabase.rpc("get_event_summary_sheet_data", { p_token: token });
    if (rpcErr) {
      setError("This link is invalid or has expired.");
      setLoading(false);
      return;
    }
    const parsed = typeof result === "string" ? JSON.parse(result) : result;
    if (parsed?.error) {
      setError("This link is invalid or has expired.");
      setLoading(false);
      return;
    }
    setError(null);
    setData(parsed);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const needsGate = useMemo(() => {
    if (!data) return false;
    const allowed = data.allowed_emails;
    if (!Array.isArray(allowed) || allowed.length === 0) return false;
    if (gatePassed) return false;
    const stored = (localStorage.getItem("sheet_viewer_email") || "").toLowerCase();
    if (stored && allowed.map((e) => e.toLowerCase()).includes(stored)) return false;
    return true;
  }, [data, gatePassed]);

  const events = data?.events || [];

  const filtered = useMemo(() => {
    let rows = [...events];
    const q = search.toLowerCase().trim();
    if (q) {
      rows = rows.filter((r) =>
        [r.name, r.venue_city, r.venue_state].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
      );
    }
    const numericKeys = ["teams", "paid", "pending", "collected", "owed"];
    rows.sort((a, b) => {
      let av, bv;
      if (sortKey === "start_date") {
        av = a.start_date ? parseLocalDate(a.start_date).getTime() : 0;
        bv = b.start_date ? parseLocalDate(b.start_date).getTime() : 0;
      } else if (sortKey === "dates") {
        av = a.start_date ? parseLocalDate(a.start_date).getTime() : 0;
        bv = b.start_date ? parseLocalDate(b.start_date).getTime() : 0;
      } else if (sortKey === "location") {
        av = `${a.venue_city ?? ""} ${a.venue_state ?? ""}`.toLowerCase();
        bv = `${b.venue_city ?? ""} ${b.venue_state ?? ""}`.toLowerCase();
      } else if (numericKeys.includes(sortKey)) {
        av = Number(a[sortKey]) || 0;
        bv = Number(b[sortKey]) || 0;
      } else {
        av = String(a[sortKey] ?? "").toLowerCase();
        bv = String(b[sortKey] ?? "").toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [events, search, sortKey, sortDir]);

  const totals = useMemo(() => {
    return {
      events: events.length,
      teams: events.reduce((s, e) => s + (Number(e.teams) || 0), 0),
      collected: events.reduce((s, e) => s + (Number(e.collected) || 0), 0),
      owed: events.reduce((s, e) => s + (Number(e.owed) || 0), 0),
    };
  }, [events]);

  const toggleSort = (key) => {
    if (key === "dates") key = "start_date";
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const exportCsv = () => {
    const headers = COLS.map((c) => c.label);
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      const vals = [
        r.name,
        fmtRange(r.start_date, r.end_date),
        [r.venue_city, r.venue_state].filter(Boolean).join(", ") || "—",
        r.teams, r.paid, r.pending, r.collected, r.owed,
      ];
      lines.push(vals.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `season_summary_${(data?.label || "season").replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading sheet…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="rounded-lg border bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-slate-800">Sheet unavailable</p>
          <p className="mt-1 text-sm text-slate-500">{error || "This link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  if (needsGate) {
    return <EmailGate allowedEmails={data.allowed_emails || []} eventName={data.label || "Season Summary"} onPassed={() => setGatePassed(true)} />;
  }

  const title = data.label || "Season Summary";

  const sortKeyForCol = (key) => (key === "dates" ? "start_date" : key);

  return (
    <div className={embedded ? "flex flex-col bg-white" : "h-screen flex flex-col overflow-hidden bg-white"}>
      {!embedded && (
        <div className="px-6 py-4 text-white flex items-center justify-between" style={{ backgroundColor: CYAN }}>
          <div>
            <h1 className="text-xl font-bold leading-tight">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button className="inline-flex items-center gap-1 rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30" onClick={exportCsv}>
              <Download className="h-4 w-4" /> CSV
            </button>
            <button className="inline-flex items-center gap-1 rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30" onClick={() => window.print()}>
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      )}

      <div className="px-6 py-2 text-sm text-white flex flex-wrap gap-x-6 gap-y-1" style={{ backgroundColor: "rgba(6,182,212,0.9)" }}>
        <span>Events: {totals.events}</span>
        <span>Total Teams: {totals.teams}</span>
        <span>Total Collected: {fmtMoney(totals.collected)}</span>
        <span>Total Owed: {fmtMoney(totals.owed)}</span>
      </div>

      <div className="bg-slate-50 px-6 py-2 flex gap-3 items-center border-b">
        <input
          className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm"
          placeholder="Search event / location"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="ml-auto text-sm text-slate-500">{filtered.length} of {events.length} events</span>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="border-collapse min-w-max text-sm">
          <thead className="sticky top-0 z-10 bg-slate-100">
            <tr>
              {COLS.map((c) => {
                const sk = sortKeyForCol(c.key);
                return (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    className={`${c.width} border px-2 py-2 text-left font-semibold text-slate-700 cursor-pointer select-none hover:bg-slate-200`}
                  >
                    {c.label}
                    {sortKey === sk ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-slate-50/60" : "bg-white"}>
                {COLS.map((c) => {
                  const cls = `${c.width} border px-2 py-1 align-top`;
                  if (c.type === "dates") return <td key={c.key} className={cls}>{fmtRange(r.start_date, r.end_date)}</td>;
                  if (c.type === "location")
                    return <td key={c.key} className={cls}>{[r.venue_city, r.venue_state].filter(Boolean).join(", ") || "—"}</td>;
                  if (c.type === "money") return <td key={c.key} className={cls}>{fmtMoney(r[c.key])}</td>;
                  if (c.type === "number") return <td key={c.key} className={cls}>{r[c.key] ?? 0}</td>;
                  return <td key={c.key} className={cls}><span className="truncate block">{r[c.key] ?? "—"}</span></td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
